import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import JsonData from "../data/data.json";
import Map from '../components/Map';
import './Home.css';

const Home = () => {

  // Safely pull from your existing JSON, with fallbacks that mirror data.json
  const missionText = JsonData.About?.mission || "To connect the world's leading brands with Pakistan's finest suppliers, and to deliver every order with the quality, openness, and on-time performance we would expect ourselves — from the first sample through to the final shipment.";
  const visionText = JsonData.About?.vision || "To Learn, Develop & Implement Best Management Practices that can be followed by rest of the Economy to Enhance the National Status and Well Being of Individual.";

  // ─── Pillars ─────────────────────────────────────────────────────────────
  const pillars = [
    {
      title: "Sourcing",
      desc: "A carefully selected network of Pakistan's leading suppliers, built up over years of working together. We match each order to the supplier best suited to it, then manage the quality, the price, and the documentation — so you work with a single partner rather than several.",
      img: "/img/Sourcing/Elleti-garment-making-Laundry-Dyeing.jpeg",
      link: "/sourcing"
    },
    {
      title: "Production",
      desc: "Our own factory turns approved designs into more than 150,000 garments a month, peaking at 5,000 a day. Because the floor is ours, we set the schedule and check the work at every stage rather than hoping someone else did.",
      img: "/img/Production/pillar-production.jpg",
      link: "/production"
    },
    {
      title: "Sustainability",
      desc: "Rooftop solar, on-site water treatment, and certified materials are part of how the factory runs day to day. Every claim on this page is verified by an external body, and the certificate behind it is available on request.",
      img: "/img/Sustainability/Sunset on Solar Panels.png",
      link: "/sustainability"
    }
  ];

  // ─── Certifications ──────────────────────────────────────────────────────
  const certifications = [
    { src: "/img/Logos/oeko.png", alt: "OEKO-TEX", label: "OEKO-TEX Certified" },
    { src: "/img/Logos/gots.png", alt: "GOTS", label: "Global Organic Textile Standard" },
    { src: "/img/Logos/grs.png", alt: "GRS", label: "Global Recycled Standard" },
    { src: "/img/Logos/bci.png", alt: "BCI", label: "Better Cotton Initiative" },
    { src: "/img/Logos/BSCI-Certification-amfori.png", alt: "BSCI", label: "BSCI / amfori" },
    { src: "/img/Logos/sedex.png", alt: "Sedex", label: "Sedex Member" },
  ];

  // ─── Partners ────────────────────────────────────────────────────────────
  const partners = [
    "kocca.png", "manifatura.png", "terranova.png", "carisma.png", "carry.png",
    "hering.png", "frabo.png", "charanga.png", "kingsley.png", "code.png",
    "dos galgos.png", "bigdude.png", "soul star.png", "noppies.png", "riu paris.png",
    "coccodrilo.png", "chicore.png", "lanidor.png", "vilanova.png", "punt roma.png",
    "zebra.png", "orsay.png", "reserved.png", "house.png", "gas.png",
    "mise au green.png", "pinkwoman.png", "blue seven.png", "authentic.png"
  ];
  const tickerItems = [...partners, ...partners];

  // ─── Framer Motion Variants ──────────────────────────────────────────────
  const fadeUpVariant = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  };

  return (
    <div className="home-modern-page">

      {/* ── Cinematic Image Hero ───────────────────────────────────────── */}
      <section className="home-cinematic-hero">
        {/* The photograph is its own layer rather than a background-image, so it
            can be positioned independently of the text bed below. */}
        <div
          className="hero-photo"
          style={{ backgroundImage: `url('/img/Production/home-hero.jpg')` }}
          aria-hidden="true"
        ></div>
        <div className="hero-scrim" aria-hidden="true"></div>

        <div className="container hero-content-wrapper">
          <motion.div
            className="hero-copy"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: "easeOut", delay: 0.2 }}
          >
            <span className="hero-eyebrow">
              <span className="hero-eyebrow-rule" aria-hidden="true"></span>
              Garment Sourcing &amp; Manufacturing — Lahore, Since 2008
            </span>

            <h1 className="hero-massive-title">
              From First Thread to <span className="hero-italic">Final Shipment.</span>
            </h1>

            <p className="hero-lede">
              A trusted supplier network and our own production floor, managed by
              one accountable partner.
            </p>

            <div className="hero-actions">
              <Link to="/production" className="hero-btn primary">
                Explore Our Capabilities
              </Link>
              <Link to="/contact" className="hero-btn ghost">
                Start an Inquiry
              </Link>
            </div>
          </motion.div>
        </div>

        <div className="hero-scroll-cue" aria-hidden="true">
          <span className="hero-scroll-label">Scroll</span>
          <span className="hero-scroll-track"><span className="hero-scroll-dot"></span></span>
        </div>
      </section>

      {/* ── The Manifesto + Vision/Mission ──────────────────────────────── */}
      <section className="home-manifesto-section">
        <div className="container">
          <motion.div
            className="manifesto-text"
            variants={fadeUpVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <h2>
              Operating since 2008, Softwood drives $60 million in monthly sales for brands in 20+ countries through our proprietary factory and trusted supplier network. As an end-to-end supply chain partner, we streamline sourcing, development, production, and quality control into a single, unified relationship.
            </h2>
            <div className="copper-divider"></div>
          </motion.div>

          {/* Vision & Mission Grid */}
          <motion.div
            className="vm-grid"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <motion.div className="vm-card" variants={fadeUpVariant}>
              <span className="vm-number">01</span>
              <h3>Our Mission</h3>
              <p>{missionText}</p>
            </motion.div>

            <motion.div className="vm-card" variants={fadeUpVariant}>
              <span className="vm-number">02</span>
              <h3>Our Vision</h3>
              <p>{visionText}</p>
            </motion.div>
          </motion.div>

          <div className="text-center" style={{ marginTop: "3rem" }}>
            <Link to="/about-us" className="editorial-link">Read Our Full Story &rarr;</Link>
          </div>
        </div>
      </section>

      {/* ── Core Pillars ────────────────────────────────────────────────── */}
      <section className="home-pillars-section">
        <div className="container">
          <motion.div
            className="section-header"
            variants={fadeUpVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <span className="copper-eyebrow">Our Capabilities</span>
            <h2 className="dark-title">The Core Pillars</h2>
          </motion.div>

          <motion.div
            className="pillars-grid"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            {pillars.map((pillar, index) => (
              <motion.div className="pillar-card" key={index} variants={fadeUpVariant}>
                <img src={pillar.img} alt={pillar.title} className="pillar-bg" />
                <div className="pillar-overlay">
                  <div className="pillar-content">
                    <h3>{pillar.title}</h3>
                    <p>{pillar.desc}</p>
                    <Link to={pillar.link} className="pillar-link">Explore {pillar.title}</Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Certifications (Dark Mode Editorial Block) ──────────────────── */}
      <section className="home-certs-section">
        <div className="container">
          <motion.div
            className="section-header"
            variants={fadeUpVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <span className="copper-eyebrow">Standards &amp; Compliance</span>
            <h2 className="light-text">Our Certifications</h2>
            <p className="light-subtitle">
              Independently audited against recognised international standards. Every certificate below is current, and the supporting documentation is available to any buyer on request.
            </p>
          </motion.div>

          <motion.div
            className="certs-grid"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            {certifications.map((cert, i) => (
              <motion.div className="cert-card" key={i} variants={fadeUpVariant}>
                <div className="cert-img-wrapper">
                  <img src={cert.src} alt={cert.alt} loading="lazy" />
                </div>
                <span className="cert-label">{cert.label}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Partners Ticker ─────────────────────────────────────────────── */}
      <section className="home-partners-section">
        <div className="container">
          <motion.div
            className="section-header text-center"
            variants={fadeUpVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            <span className="copper-eyebrow">Global Clientele</span>
            <h2>Trusted by Brands in 20+ Countries</h2>
            <div className="copper-divider center-div"></div>
          </motion.div>
        </div>

        <motion.div
          className="ticker-track-outer"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.2 }}
        >
          <div className="ticker-track">
            {tickerItems.map((img, i) => (
              <div className="ticker-item" key={i}>
                <img
                  src={`/img/home/Partners/${img}`}
                  alt={img.replace(/\.png$/i, '')}
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ── Map (Seamless Integration) ──────────────────────────────────── */}
      <div className="home-map-wrapper">
        <Map />
      </div>

    </div>
  );
};

export default Home;